import { type PlayingPace } from '@league/shared/schemas';
import { PACE_OPTIONS } from '../../content/playing-preferences';
import { LeagueSubs } from './LeagueSubs';
import { useSearchParams } from 'react-router-dom';
import { comparePeople } from './people-display';
import type {
  EnrollmentRecord as Enrollment,
  InvitePlayersResponse,
  RosterEntry,
  RosterResponse,
} from '@league/shared/contract';
import { rosterLockState } from '@league/shared/roster-lock';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { Button, ErrorNote, Field, Input } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import { ImportRoster } from './ImportRoster';
import { SeatingGroups } from './SeatingGroups';
import { RosterLockBanner } from './RosterLockBanner';
import { RosterSummary } from './RosterSummary';
import { RosterTable } from './RosterTable';
import { SegmentedControl } from '../../components/SegmentedControl';
import { REMOVE_NOTE, removeQuestion } from './roster-status';
import { ROSTER_LOCK_IN_V1 } from '@league/shared/features';

/*
  `Enrollment`, `Player`, `RosterEntry` and `SeatingGroup` were all declared HERE, by hand, and the
  comment that used to sit on the status union explained why that was a problem: `registered` was
  added to the database, the repository and the projection, and this file's copy of the union still
  had three members, so every self-registration fell through every filter and rendered nowhere.

  They now come from the contract — the same declaration the server's projection is checked against.
  Adding a fifth status is a compile error in this file, which is the only useful moment to hear it.
*/

/**
 * The roster.
 *
 * Three groups, always in this order: playing, waiting, gone. A director scanning this on a phone
 * on league session needs the count and the names, and nothing else competing for attention.
 */
const LIST_TABS = [
  ['enrolled', 'Roster'],
  ['waitlist', 'Waitlist'],
  ['subs', 'Subs'],
] as const;

/*
  THE SEARCH BOX CARRIES THE FOCUS, NOT THE INPUT INSIDE IT — owner, 2026-10-08: one outline, not a box
  inside a box. The box's own edge turns solid navy on focus (border plus a 2px ring), the same navy
  and the same strength as the house :focus-visible outline, so nothing is lost; only the input's
  duplicate outline goes. A deliberate, single exception to the outline rule.
*/
// eslint-disable-next-line no-restricted-syntax -- the wrapping label shows a 2px navy focus edge instead (see above)
const SEARCH_INPUT = 'min-w-0 flex-1 border-0 bg-transparent text-[14px] text-ink outline-none placeholder:text-ink-3';

export function Roster({ leagueId, playerCap, timeZone }: { leagueId: string; playerCap: number; timeZone: string }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [panel, setPanel] = useState<'none' | 'add' | 'import'>(() => searchParams.get('panel') === 'import' ? 'import' : searchParams.get('panel') === 'add' ? 'add' : 'none');

  /**
   * Off on every load, deliberately. An override that survived a refresh would be an override
   * somebody turned on last Tuesday and forgot, which is the same as no override at all.
   */
  const [overriding, setOverriding] = useState(false);

  /**
   * Which groups to show, and a search across them. Both live here rather than in the URL: they are
   * a way of looking at one list for a moment, not a place somebody links to.
   */
  const [showing, setShowing] = useState<'enrolled' | 'waitlist' | 'subs'>(() => {
    const list = searchParams.get('list');
    return list === 'subs' || list === 'waitlist' ? list : 'enrolled';
  });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const chooseList = (key: (typeof LIST_TABS)[number][0]) => {
    setShowing(key);
    const next = new URLSearchParams(searchParams);
    next.set('list', key);
    next.delete('player');
    setSearchParams(next);
  };

  const roster = useQuery({
    queryKey: ['roster', leagueId],
    queryFn: () =>
      apiFetch<RosterResponse>(`/api/leagues/${leagueId}/roster`),
  });

  const all = roster.data?.roster ?? [];

  /**
   * The search narrows what is DISPLAYED, never what is counted. A summary that changed as she
   * typed would be a summary she could not trust — "24 of 28" has to mean the league, not the
   * seven rows currently matching "ch".
   */
  const needle = search.trim().toLowerCase();
  const entries = needle
    ? all.filter((e) =>
        `${e.player.firstName} ${e.player.lastName} ${e.player.email ?? ''}`
          .toLowerCase()
          .includes(needle),
      )
    : all;

  /*
    A SEAT HELD WHILE SHE PAYS ONLINE (2026-10-06) is a player on this league's roster, unpaid.
    A hold that has run out holds nothing, and the server drops it within minutes. A registration
    with nothing to pay online is written `enrolled` (2026-10-25), so `registered` without a hold
    no longer exists — the panel that listed those rows was removed with the migration that moved
    them onto the roster.
  */
  const now = Date.now();
  const held = (e: RosterEntry) => e.enrollment.status === 'registered' && e.enrollment.paymentDueAt !== null;
  const holdLive = (e: RosterEntry) => held(e) && Date.parse(e.enrollment.paymentDueAt!) > now;
  const onRoster = (e: RosterEntry) => e.enrollment.status === 'enrolled' || holdLive(e);
  const enrolled = entries.filter(onRoster).sort((a, b) => comparePeople(a.player, b.player));
  const waiting = entries.filter((e) => e.enrollment.status === 'waitlist').sort((a, b) => comparePeople(a.player, b.player));

  const matchingIds = (showing === 'waitlist' ? waiting : showing === 'enrolled' ? enrolled : []).map(entry=>entry.enrollment.id);
  // A filter/tab change narrows the operation; hidden selections are never removed.
  const activeSelection = selected.filter(id=>matchingIds.includes(id));
  const selectPlayer = (id:string, checked:boolean) => setSelected(previous=>checked ? [...new Set([...previous,id])] : previous.filter(value=>value!==id));

  /** Counted from everybody, for the same reason. */
  const seatHolders = all.filter(
    (e) => e.enrollment.status === 'enrolled' || (e.enrollment.status === 'registered' && (!held(e) || holdLive(e))),
  );
  const currentPlayers = all.filter((e) => e.enrollment.status === 'enrolled');
  const show = (group: 'enrolled' | 'waitlist') =>
    showing === group;

  /** Membership excludes earlier registrations; admission still reserves their spots. */
  const seatsTaken = currentPlayers.length;
  const seatsLeft = playerCap - seatHolders.length;

  /**
   * Computed from the raw date with the SAME pure module the repository enforces with, so the
   * banner and the server's refusal can never disagree about what time it is.
   */
  const lock = rosterLockState(
    roster.data?.rosterLockAt ? new Date(roster.data.rosterLockAt) : null,
    new Date(),
  );

  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['roster', leagueId] }),
    queryClient.invalidateQueries({ queryKey: ['director-leagues'] }),
    queryClient.invalidateQueries({ queryKey: ['director-dashboard'] }),
  ]);

  const promote = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiPost(`/api/enrollments/${enrollmentId}/promote`, { override: overriding }),
    onSuccess: refresh,
  });

  /**
   * WHO THE CONFIRMATION IS ABOUT, held once for the page rather than once per row.
   *
   * There is ONE dialog on this tab and every Remove button opens it. A roster of eighteen would
   * otherwise mount eighteen modals, seventeen of which can never be shown.
   */
  const [removing, setRemoving] = useState<{
    enrollmentId: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const confirmRemoval = useRef<HTMLDialogElement>(null);
  const rosterHeading = useRef<HTMLHeadingElement>(null);

  const drop = useMutation({
    mutationFn: (enrollmentId: string) =>
      apiPost(`/api/enrollments/${enrollmentId}/drop`, { override: overriding }),
    /*
      The dialog closes on SUCCESS ONLY. A refusal — the roster lock is the one that actually
      happens — has to be read where the decision was made, so the error renders inside the dialog
      and the dialog stays put with her choice still on screen.
    */
    onSuccess: async () => {
      await refresh();
      confirmRemoval.current?.close();
      setRemoving(null);
      rosterHeading.current?.focus();
    },
  });

  /*
    SIGN-IN INVITATIONS. Ruled 2026-09-25: adding or importing a player sends her nothing — this is
    where the director decides she should hear about her account. Named players from a row, or
    everyone on the roster not yet invited from the bar above it; the server never re-sends from the
    second, so pressing it twice cannot mail the room twice.
  */
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: (playerIds?: string[]) =>
      apiPost<InvitePlayersResponse>(
        `/api/leagues/${leagueId}/roster/invitations`,
        playerIds ? { playerIds } : {},
      ),
    onSuccess: async ({ invited, notSent }) => {
      /* What did not go is said (2026-10-15); a sent invitation says so on its row. */
      setInviteNote(
        notSent > 0
          ? `${notSent} invitation${notSent === 1 ? '' : 's'} could not be sent. Try again in a minute.${invited > 0 ? ` ${invited} went.` : ''}`
          : null,
      );
      await refresh();
    },
  });

  const askToRemove = (enrollmentId: string, player: { firstName: string; lastName: string }) => {
    drop.reset();
    setRemoving({ enrollmentId, firstName: player.firstName, lastName: player.lastName });
    confirmRemoval.current?.showModal();
  };

  return (
    <section className="flex flex-col gap-4">
      <RosterSummary
        seatsTaken={seatsTaken}
        playerCap={playerCap}
        waiting={all.filter((e) => e.enrollment.status === 'waitlist').length}

      />

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        {/*
          ⚠ tabIndex -1 SO THERE IS SOMEWHERE FOR FOCUS TO LAND AFTER A REMOVAL. Closing a dialog
          normally hands focus back to whatever opened it — but the thing that opened this one was
          her row's Remove button, and a successful removal takes that row off the page. Focus fell
          to <body>, so a director on a keyboard finished the job and then had to tab in from the
          top of the document. The heading of the list she just changed is the honest place to
          resume, and a screen reader reads "Roster" as she arrives.
        */}
        <h2 ref={rosterHeading} tabIndex={-1} className="font-display text-xl leading-none">
          {showing === 'subs' ? 'Subs' : showing === 'waitlist' ? 'Waitlist' : 'Roster'}
        </h2>
        {showing !== 'subs' ? <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            className="whitespace-nowrap"
            onClick={() => setPanel((p) => (p === 'import' ? 'none' : 'import'))}
          >
            Import Players
          </Button>
          {/* One button, one job: Cancel lives in the form beside its Add, as on the Subs page. */}
          {panel === 'add' ? null : (
            <Button className="whitespace-nowrap" onClick={() => setPanel('add')}>
              Add Player
            </Button>
          )}
        </div> : null}
      </div>

      {showing !== 'subs' && panel === 'add' ? (
        <AddPlayerForm
          leagueId={leagueId}
          seatsLeft={seatsLeft}
          onAdded={() => {
            void refresh();
            setPanel('none');
          }}
          onCancel={() => setPanel('none')}
        />
      ) : null}

      {showing !== 'subs' && panel === 'import' ? (
        <ImportRoster
          leagueId={leagueId}
          seatsLeft={seatsLeft}
          onImported={refresh}
          onCancel={() => setPanel('none')}
        />
      ) : null}

      {/* Above the errors, because it is usually the reason for one. */}
      {/* Not part of V1 — see `@league/shared/features`. */}
      {ROSTER_LOCK_IN_V1 ? (
        <RosterLockBanner state={lock} overriding={overriding} onToggleOverride={setOverriding} />
      ) : null}

      {(
        <>
          <div className="flex flex-wrap items-center gap-3">
            {/*
              A SEGMENTED CONTROL, not a dropdown — owner, 2026-10-08. The three lists that answer
              "who am I looking at" stay on screen at once, and the current one is the thing she
              most needs to see. Tabs to a screen reader; Left and Right move between them.
            */}
            <SegmentedControl label="Player list" options={LIST_TABS} value={showing} onChange={chooseList} />

            <label className="flex min-h-11 min-w-[220px] flex-1 items-center gap-2 rounded-[10px] border border-sand bg-paper px-3.5 has-[:focus-visible]:border-deep has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-deep">
              <span className="sr-only">Search players</span>
              <svg aria-hidden="true" viewBox="0 0 16 16" className="h-4 w-4 shrink-0 text-ink-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <circle cx="7" cy="7" r="5" />
                <path d="M11 11l3.5 3.5" />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search players"
                className={SEARCH_INPUT}
              />
            </label>
          </div>

          {showing !== 'subs' && needle && entries.length === 0 ? (
            <p className="rounded-md border border-dashed border-light px-5 py-6 text-center text-[13px] text-ink-3">
              Nobody on this roster matches “{search.trim()}”.
            </p>
          ) : null}
        </>
      )}

      {promote.error ? (
        <ErrorNote>
          {promote.error instanceof ApiError ? promote.error.message : 'Could not promote.'}
        </ErrorNote>
      ) : null}


      {inviteNote ? <p role="status" className="text-[13px] text-ink-2">{inviteNote}</p> : null}
      {invite.error ? (
        <ErrorNote>
          {invite.error instanceof ApiError ? invite.error.message : 'Could not send the invitation.'}
        </ErrorNote>
      ) : null}

      {roster.isPending ? <p className="text-sm text-ink-3">Loading…</p> : null}

      {showing !== 'subs' && !roster.isPending && entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
          No players yet. Add a player or import a roster.
        </p>
      ) : null}

      {showing === 'subs' ? <LeagueSubs leagueId={leagueId} roster={all} search={search} /> : null}


      {show('enrolled') && enrolled.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="font-ui text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-2">
            Roster
          </h3>
          <RosterTable
            entries={enrolled}
            leagueId={leagueId}
            zone={timeZone}
            selected={activeSelection}
            onSelect={selectPlayer}
            onSelectAll={(checked) => setSelected(checked ? matchingIds : [])}
            onInvite={(playerIds) => invite.mutateAsync(playerIds)}
            onRemove={(e) => askToRemove(e.enrollment.id, e.player)}
            onChanged={() => { setSelected([]); void refresh(); }}
          />

        </div>
      ) : null}

      {show('waitlist') && waiting.length > 0 ? (
        <Group
          title="Waitlist"
          note={
            seatsLeft > 0
              ? `${seatsLeft} spot${seatsLeft === 1 ? '' : 's'} available`
              : 'The league is full. Remove someone from the league to open a seat.'
          }
        >
          <RosterTable
            entries={waiting}
            leagueId={leagueId}
            zone={timeZone}
            selected={activeSelection}
            onSelect={selectPlayer}
            onSelectAll={(checked) => setSelected(checked ? matchingIds : [])}
            onInvite={(playerIds) => invite.mutateAsync(playerIds)}
            onRemove={(e) => askToRemove(e.enrollment.id, e.player)}
            onChanged={() => { setSelected([]); void refresh(); }}
            waitlist={{ onPromote: (e) => promote.mutateAsync(e.enrollment.id), promoteDisabled: seatsLeft <= 0 || promote.isPending, seatsLeft }}
          />
        </Group>
      ) : null}

      {show('enrolled') ? (
        <SeatingGroups
          leagueId={leagueId}
          requests={roster.data?.sitWithRequests ?? []}
          people={all.map(({ enrollment, player }) => ({
            enrollmentId: enrollment.id,
            name: `${player.firstName} ${player.lastName}`.trim(),
            firstName: player.firstName,
            lastName: player.lastName,
            email: player.email,
            enrolled: enrollment.status === 'enrolled',
          }))}
        />
      ) : null}

      {/*
        A NATIVE <dialog>, OPENED WITH showModal() — the same control the Play Together group on
        this very tab already uses, so the two confirmations on one screen behave alike.

        What `showModal` gives us that a hand-rolled panel would have to be taught: the rest of the
        page goes inert, focus is trapped, Escape closes, and closing returns focus to whatever
        opened it — which here is the Remove button on her row, seventeen rows down.
      */}
      <dialog
        ref={confirmRemoval}
        aria-labelledby="remove-player-title"
        /*
          The width is `calc(100% - 2rem)` and not just `max-w-md`: a 448px box centred in a 390px
          phone has no side margin at all and sits corner to corner, which reads as the page having
          broken rather than as a card. This keeps a 16px gutter on a phone and changes nothing on a
          laptop, where the max-width is what binds.
        */
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-md border border-light bg-paper p-6 text-ink backdrop:bg-black/30"
        /* Escape must not abandon a request that is already in flight. */
        onCancel={(event) => {
          if (drop.isPending) event.preventDefault();
          else setRemoving(null);
        }}
      >
        <h4 id="remove-player-title" className="font-display text-xl">
          {removeQuestion(`${removing?.firstName ?? ''} ${removing?.lastName ?? ''}`.trim())}
        </h4>
        {/*
          ⚠ NAMED, NOT "ARE YOU SURE?" — the house rule, stated at length in `CloseOutTab`: "are you
          sure?" tells her nothing she did not already know and trains her to click through it. On a
          roster of eighteen the only thing worth confirming is that she has the right row.

          And it says what actually happens. Removal is REVERSIBLE — Removed Players is linked right
          behind this dialog — which is what makes a quick decision a safe one rather than a brave
          one. Nothing here is typed to confirm; that ceremony belongs in front of things that cannot
          be undone.
        */}
        <p className="mt-2 text-sm text-ink-2">
          {REMOVE_NOTE}
        </p>
        {drop.error ? (
          <ErrorNote>
            {drop.error instanceof ApiError ? drop.error.message : 'Could not remove that player.'}
          </ErrorNote>
        ) : null}
        <div className="mt-5 flex gap-3">
          {/*
            ⚠ FOCUS LANDS ON KEEP, NOT ON REMOVE. If somebody opened this by accident, the next key
            they press must not finish the job — and Enter on a freshly opened confirmation is
            exactly that key. Both are real buttons: an underlined "Keep her" beside a bordered
            "Remove" would lean the whole dialog toward the destructive answer.
          */}
          <Button
            autoFocus
            variant="secondary"
            disabled={drop.isPending}
            onClick={() => {
              confirmRemoval.current?.close();
              setRemoving(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={drop.isPending}
            onClick={() => {
              if (removing) drop.mutate(removing.enrollmentId);
            }}
          >
            {drop.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </dialog>


    </section>
  );
}


function Group({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-3">
        <h3 className="font-ui text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-2">
          {title}
        </h3>
        {note ? <span className="text-[12px] text-ink-3">{note}</span> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * Adding one player. The email is what matters: the API looks it up first, so someone who played
 * in another market keeps the same national record rather than becoming a duplicate.
 */
function AddPlayerForm({
  leagueId,
  seatsLeft,
  onAdded,
  onCancel,
}: {
  leagueId: string;
  seatsLeft: number;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [paceOverride, setPaceOverride] = useState<PlayingPace | ''>('');

  const add = useMutation({
    mutationFn: () =>
      apiPost<{ enrollment: Enrollment; reusedExistingPlayer: boolean }>(
        `/api/leagues/${leagueId}/roster`,
        { firstName, lastName, email, ...(paceOverride ? { paceOverride } : {}) },
      ),
    onSuccess: () => {
      setFirstName('');
      setLastName('');
      setEmail('');
      onAdded();
    },
  });

  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-light bg-sand p-4"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      {seatsLeft <= 0 ? (
        <p className="text-[13px] text-ink-2">
          The league is full — whoever you add next goes on the waitlist.
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="First name" htmlFor="firstName">
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
            required
          />
        </Field>
        <Field label="Last name" htmlFor="lastName">
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </Field>
      </div>

      <Field
        label="Email"
        htmlFor="playerEmail"
        hint="Use the player’s usual email. Adding a player does not send an invitation."
      >
        <Input
          id="playerEmail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Field>

      <Field label="Playing pace" htmlFor="pace">
        <select id="pace" value={paceOverride} onChange={e => setPaceOverride(e.target.value as PlayingPace | '')} className="min-h-11 w-full min-w-0 rounded-md border border-control-border bg-paper px-3 text-[15px]">
          <option value="">Use player’s preference</option>
          {PACE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {paceOverride ? <p className="text-sm text-ink-2">{PACE_OPTIONS.find(p => p.value === paceOverride)?.description}</p> : null}
      </Field>

      {add.error ? (
        <ErrorNote>
          {add.error instanceof ApiError ? add.error.message : 'Could not add that player.'}
        </ErrorNote>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={add.isPending}>
          {add.isPending ? 'Adding…' : seatsLeft > 0 ? 'Add to league' : 'Add to waitlist'}
        </Button>
        <Button variant="secondary" disabled={add.isPending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

